import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, BASE_URL, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { BackButton } from "../shared/BackButton";
import { isoToLocalInputValue, localInputValueToIso } from "../shared/utils";
import { buildRegisterTabs, loadTabScopeContext, createTabInspection } from "./tabInspections";

export function CreateInspection({ onBack, onCreate }) {
  const [sites, setSites] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedSite, setSelectedSite] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [inspectionType, setInspectionType] = useState("");
  const [date, setDate] = useState(isoToLocalInputValue(new Date().toISOString()));
  const [inspector, setInspector] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [technicians, setTechnicians] = useState([]);
  const [assignedTo, setAssignedTo] = useState("");
  const [additionalTechs, setAdditionalTechs] = useState([]);
  const [flexible, setFlexible] = useState(false);
  const [registerTabs, setRegisterTabs] = useState([]);
  const [tabsLoading, setTabsLoading] = useState(false);
  const [selectedTabKeys, setSelectedTabKeys] = useState(() => new Set());

  const toggleTech = (id, name) => {
    const idStr = String(id);
    if (assignedTo === idStr) {
      // Deselect lead
      if (additionalTechs.length > 0) {
        setAssignedTo(String(additionalTechs[0]));
        setAdditionalTechs(additionalTechs.slice(1));
        // Also update inspector name to new lead
        const newLead = technicians.find(t => String(t.id) === String(additionalTechs[0]));
        if (newLead) setInspector(newLead.first_name && newLead.last_name ? `${newLead.first_name} ${newLead.last_name}` : newLead.username);
      } else {
        setAssignedTo("");
      }
    } else if (additionalTechs.includes(idStr)) {
      setAdditionalTechs(additionalTechs.filter(t => t !== idStr));
    } else {
      if (!assignedTo) {
        setAssignedTo(idStr);
        if (!inspector.trim()) setInspector(name);
      } else {
        setAdditionalTechs([...additionalTechs, idStr]);
      }
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/sites/`)
      .then((res) => setSites(res.data))
      .catch(() => {});
    axios
      .get(`${BASE_URL}/auth/technicians/`)
      .then((res) => setTechnicians(res.data))
      .catch(() => {});
  }, []);

  // The register's sections drive the split — each becomes its own inspection.
  useEffect(() => {
    if (!selectedSite) {
      setRegisterTabs([]);
      setSelectedTabKeys(new Set());
      return;
    }
    setTabsLoading(true);
    axios
      .get(`${API}/site-areas/?site=${selectedSite.id}`)
      .then((res) => {
        const nextTabs = buildRegisterTabs(selectedSite, res.data);
        setRegisterTabs(nextTabs);
        setSelectedTabKeys(new Set(nextTabs.map((t) => t.key)));
      })
      .catch(() => {
        setRegisterTabs([]);
        setSelectedTabKeys(new Set());
      })
      .finally(() => setTabsLoading(false));
  }, [selectedSite]);

  const toggleTab = (key) => {
    setSelectedTabKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedTabs = registerTabs.filter((t) => selectedTabKeys.has(t.key));

  const filtered = sites.filter(
    (s) =>
      s.site_name.toLowerCase().includes(search.toLowerCase()) ||
      s.site_address.toLowerCase().includes(search.toLowerCase()),
  );

  const handleCreate = async () => {
    if (!selectedSite) {
      setError("Please select a site.");
      return;
    }
    if (!inspectionType) {
      setError("Please select an inspection type.");
      return;
    }
    if (!inspector.trim()) {
      setError("Technician name is required.");
      return;
    }
    if (!date) {
      setError("Inspection date is required.");
      return;
    }
    if (registerTabs.length === 0) {
      setError("This site has no asset register sections to inspect.");
      return;
    }
    if (selectedTabs.length === 0) {
      setError("Select at least one section.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      // One inspection per Asset Register section, so each flows down the
      // pipeline to its own report, records entry and rectifications
      // independently — rather than one merged sheet covering the whole site.
      const ctx = await loadTabScopeContext(selectedSite.id);
      const isoDate = localInputValueToIso(date);
      const created = [];
      for (const tab of selectedTabs) {
        const result = await createTabInspection({
          siteId: selectedSite.id,
          tab,
          ctx,
          inspectionType,
          date: isoDate,
          inspectorName: inspector,
          assignedTo,
          additionalTechs,
          notes,
          flexible,
          skipIfEmpty: true,
        });
        if (result) created.push(result);
      }
      if (created.length === 0) {
        setError("No assets at this site are due for that inspection type.");
        setSaving(false);
        return;
      }
      onCreate(created.length === 1 ? created[0].id : null);
    } catch (e) {
      setError("Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={styles.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <BackButton onClick={onBack} />
          <div style={styles.pageTitle}>Create Inspection</div>
        </div>
      </div>
      <div style={styles.content}>
        {step === 1 && (
          <div style={styles.card}>
            <div
              style={{
                padding: "20px 24px",
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "4px",
                }}
              >
                Step 1 — Select Site
              </div>
              <div style={{ fontSize: "13px", color: "#999" }}>
                Search for the site you want to inspect
              </div>
            </div>
            <div style={styles.searchBar}>
              <input
                style={{ ...styles.searchInput, maxWidth: "50%" }}
                placeholder="Search by site name or address..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div
              style={{ ...styles.tableHeader, gridTemplateColumns: "2fr 3fr" }}
            >
              <div>Site Name</div>
              <div>Address</div>
            </div>
            {filtered.length === 0 ? (
              <div style={styles.loading}>No sites found.</div>
            ) : (
              filtered.map((site) => (
                <div
                  key={site.id}
                  onClick={() => {
                    setSelectedSite(site);
                    setStep(2);
                  }}
                  onMouseEnter={() => setHovered(site.id)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    ...styles.tableRow(hovered === site.id),
                    gridTemplateColumns: "2fr 3fr",
                  }}
                >
                  <div style={{ fontWeight: "500", color: colors.text }}>
                    {site.site_name}
                  </div>
                  <div style={{ fontSize: "13px", color: "#666" }}>
                    {site.site_address}
                  </div>
                </div>
              ))
            )}
            <div style={styles.count}>
              {filtered.length} Site{filtered.length !== 1 ? "s" : ""}
            </div>
          </div>
        )}
        {step === 2 && selectedSite && (
          <div style={{ maxWidth: "600px" }}>
            <div style={{ ...styles.card, marginBottom: "16px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  backgroundColor: "#f0faf0",
                  borderBottom: `1px solid ${colors.border}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontWeight: "600", color: colors.green }}>
                    ✓ Site Selected
                  </div>
                  <div style={{ fontSize: "14px", fontWeight: "500" }}>
                    {selectedSite.site_name}
                  </div>
                  <div style={{ fontSize: "13px", color: "#666" }}>
                    {selectedSite.site_address}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedSite(null);
                    setStep(1);
                  }}
                  style={{
                    background: "none",
                    border: `1px solid ${colors.border}`,
                    borderRadius: "6px",
                    padding: "4px 12px",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  Change
                </button>
              </div>
            </div>
            <div style={styles.card}>
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <div style={{ fontSize: "16px", fontWeight: "600" }}>
                  Step 2 — Inspection Details
                </div>
              </div>
              <div style={{ padding: "24px" }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Inspection Type *</label>
                  <div style={{ display: "flex", gap: "12px" }}>
                    {[
                      ["monthly", "Monthly"],
                      ["bi_annual", "Bi-Annual"],
                      ["annual", "Annual"],
                    ].map(([val, label]) => (
                      <div
                        key={val}
                        onClick={() => setInspectionType(val)}
                        style={{
                          flex: 1,
                          padding: "16px",
                          border: `2px solid ${inspectionType === val ? colors.blue : colors.border}`,
                          borderRadius: "8px",
                          cursor: "pointer",
                          textAlign: "center",
                          backgroundColor:
                            inspectionType === val ? "#eff6ff" : "white",
                          transition: "all 0.2s",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "600",
                            color:
                              inspectionType === val ? colors.blue : colors.text,
                          }}
                        >
                          {label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>
                    Sections — each becomes its own inspection
                  </label>
                  {tabsLoading ? (
                    <div style={{ fontSize: "13px", color: colors.slate }}>
                      Loading sections...
                    </div>
                  ) : registerTabs.length === 0 ? (
                    <div style={{ fontSize: "13px", color: colors.red }}>
                      This site has no asset register sections yet.
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        {registerTabs.map((tab) => {
                          const picked = selectedTabKeys.has(tab.key);
                          return (
                            <button
                              key={tab.key}
                              type="button"
                              onClick={() => toggleTab(tab.key)}
                              style={{
                                padding: "6px 14px",
                                borderRadius: "20px",
                                border: `2px solid ${picked ? colors.activeBlue : colors.border}`,
                                backgroundColor: picked ? "#eff6ff" : "white",
                                color: picked ? colors.activeBlue : colors.slate,
                                fontSize: "13px",
                                fontWeight: "600",
                                cursor: "pointer",
                                opacity: picked ? 1 : 0.55,
                              }}
                            >
                              {tab.label}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: "11px", color: colors.slate, marginTop: "6px" }}>
                        {selectedTabs.length} separate inspection
                        {selectedTabs.length !== 1 ? "s" : ""} will be created.
                        Tap a section to leave it out. Sections with nothing due
                        for this type are skipped.
                      </div>
                    </>
                  )}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Inspection Date & Time *</label>
                    <input
                      style={inputStyle}
                      type="datetime-local"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Technician Name *</label>
                    <input
                      style={inputStyle}
                      value={inspector}
                      onChange={(e) => setInspector(e.target.value)}
                      placeholder="e.g. John Smith"
                    />
                  </div>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Assign Technicians</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {technicians.map((tech) => {
                      const idStr = String(tech.id);
                      const name = tech.first_name && tech.last_name
                        ? `${tech.first_name} ${tech.last_name}`
                        : tech.username;
                      const isLead = assignedTo === idStr;
                      const isAdditional = additionalTechs.includes(idStr);
                      const selected = isLead || isAdditional;
                      return (
                        <button
                          key={tech.id}
                          type="button"
                          onClick={() => toggleTech(tech.id, name)}
                          style={{
                            padding: "6px 14px",
                            borderRadius: "20px",
                            border: `2px solid ${selected ? (isLead ? colors.teal : colors.amber) : colors.border}`,
                            backgroundColor: selected ? (isLead ? "#e0f2fe" : "#fef3c7") : "white",
                            color: selected ? (isLead ? colors.teal : "#92400e") : colors.slate,
                            fontWeight: "600",
                            fontSize: "13px",
                            cursor: "pointer",
                          }}
                        >
                          {name}{isLead ? " · Lead" : isAdditional ? " · + Tech" : ""}
                        </button>
                      );
                    })}
                  </div>
                  {(assignedTo || additionalTechs.length > 0) && (
                    <div style={{ fontSize: "11px", color: colors.slate, marginTop: "6px" }}>
                      First selected = Lead tech. Tap again to remove.
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={flexible}
                      onChange={e => setFlexible(e.target.checked)}
                    />
                    <span style={{ fontSize: "13px", color: colors.text }}>Flexible — do anytime</span>
                  </label>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>General Notes</label>
                  <textarea
                    style={{
                      ...inputStyle,
                      height: "80px",
                      resize: "vertical",
                    }}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any general notes about this inspection..."
                  />
                </div>
                {error && (
                  <div
                    style={{
                      color: colors.red,
                      fontSize: "13px",
                      marginBottom: "12px",
                    }}
                  >
                    {error}
                  </div>
                )}
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <BackButton onClick={() => setStep(1)} />
                  <button
                    onClick={handleCreate}
                    disabled={saving}
                    style={styles.addButton}
                  >
                    {saving
                      ? "Creating..."
                      : selectedTabs.length > 1
                        ? `Create ${selectedTabs.length} Inspections`
                        : "Create Inspection"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
